import { AppLayout } from "components/AppLayout/AppLayout";
import { QuickNavModal } from "components/QuickNavModal/QuickNavModal";
import { BOOKS_QUICK_NAV_PAGES } from "constants/quickNav/books";
import { INDIA_COUNTRY_CODE } from "constants/countryCodes";
import { ChatLeftNav } from "components/ChatLeftNav/ChatLeftNav";
import Loader from "components/DesignSystem/Loader/loader";
import { EntitySelector } from "components/EntitySelector/EntitySelector";
import { GroupSelectorWithDefaultGroup } from "components/GroupSelector/GroupSelectorWithDefaultGroup";
import { Accounting } from "components/icons/LeftNav/Books/Accounting";
import { BookkeepingServices } from "components/icons/LeftNav/Books/BookkeepingServices";
import { DataSources as DataSourcesIcon } from "components/icons/LeftNav/Books/DataSources";
import { Transactions as TransactionsIcon } from "components/icons/LeftNav/Books/Transactions";
import { Filings as FilingsIcon } from "components/icons/LeftNav/Filings";
import { Transfers } from "components/icons/LeftNav/Intragroup/Transfers";
import { Ticket } from "components/icons/LeftNav/Ticket";
import { InkleBooksLogo } from "components/icons/Logos/InkleBooksLogo";
import { InkleBooksSymbol } from "components/icons/Logos/InkleBooksSymbol";
import { ReconciliationTxn } from "components/Reconciliation/ReconciliationTxn";
import { AIReconciliation } from "components/ChatCard/AIReconcileCard/AIReconciliation";
import { ReportViewWithId } from "components/Reports/ReportViewById";
import { RoutesAccrossApps } from "components/RoutesAcrossApps/RoutesAcrossApps";
import {
  LeftBar,
  LeftBarItem,
  LeftBarLogo,
  useLeftBar,
} from "components/Sidebar/LeftBar";
import { SidebarStickyGroup } from "components/Sidebar/SidebarStickyGroup";
import { TopBar } from "components/TopBar/TopBar";
import InitiateTransferModal from "components/TPPayments/TransferModal/InitiateTransferModal";
import {
  ACTION_ITEMS_CLICKED_FROM_SIDEBAR,
  CHAT_CLICKED_FROM_SIDEBAR,
  DOCUMENTS_CLICKED_FROM_SIDEBAR,
} from "constants/analyticsEvents";
import { YYYY_MM_DD } from "constants/date";
import { useCurrentAppContext } from "contexts/CurrentAppContext";
import dayjs from "dayjs";
import { useAnalytics } from "hooks/useAnalytics";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useMyPermissions } from "hooks/useMyPermissions";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useTransactionsFilter } from "hooks/useTransactionsFilter";
import { Invoices } from "pages/TPPayments/Invoices";
import { parse, stringify } from "qs";
import React, { useEffect, useMemo, useRef } from "react";
import {
  matchPath,
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import { RestrictedRoute } from "RestrictedRoute";
import { DashboardBanner } from "signInSignUp/DashboardBanner";
import "static/styles/containers/dashboard.css";
import { useBankConnectionsPrefetch } from "store/apis/bankConnections";
import { useBooksConnectionsPrefetch } from "store/apis/booksConnections";
import { useReportsPrefetch } from "store/apis/reports";
import { useTransactionApisPrefetch } from "store/apis/transactions";
import { BillsAndVendors } from "../BillsAndVendors/BillsAndVendors";
import { BooksHome } from "../BooksHome/BooksHome";
import { DataSources } from "../DataSources/DataSources";
import { FinancialClosing } from "../FinancialClosing/FinancialClosing";
import { FinancialClosingDetails } from "../FinancialClosing/FinancialClosingDetails";
import { FixedAsset } from "../FixedAsset/FixedAsset";
import { GeneralLedgerApp } from "../GeneralLedger/GeneralLedger";
import { TrialBalancePage } from "../TrialBalance/TrialBalance";
import { InvoicesHome } from "../Invoicing/InvoicesHome";
import { InvoiceSendPage } from "../Invoicing/InvoiceSendPage";
import { MyProducts } from "../MyProducts/MyProducts";
import { Reports } from "../Reports/Reports";
import { RevenueMetrics } from "../RevenueMetrics/RevenueMetrics";
import { Services } from "../Services/Services";
import { TransactionsPage } from "../Transactions/Transactions";
import { VendorDetailPage } from "../BillsAndVendors/VendorDetailPage";
import { Financials as FinancialsIcon } from "components/icons/LeftNav/Books/Financials";
import { SavedReport } from "../SavedReports/SavedReports";
import { Revenue as RevenueIcon } from "components/icons/LeftNav/Books/Revenue";
import { Customers } from "../Revenue/Customers/Customers";
import { Expenses as ExpensesIcon } from "components/icons/LeftNav/Books/Expenses";
import { Vendors } from "../Expenses/Vendors";
import { ChartOfAccounts } from "../Accounting/ChartOfAccounts";
import { JournalEntries } from "../Accounting/JournalEntries";
import { Reconciliation } from "../Accounting/Reconciliation";
import { HomeIcon } from "components/icons/LeftNav/Books/HomeIcon";
import { DocumentIcon } from "components/icons/DocumentIcon";
import { SubMenu } from "components/Sidebar/SubMenu";
import { IntragroupServices } from "components/IntragroupServices/IntragroupServices";
import { BooksSettings } from "../Settings/BooksSettings";
import { NewCustomReportTemplate } from "../Settings/NewCustomReportTemplate";
import { CustomReportViewOnly } from "components/Reports/CustomReports/CustomReportViewOnly";
import { CustomReports } from "components/Reports/CustomReports/CustomReports";
import { Settings as BooksSettingsIcon } from "components/icons/Navbar/Settings";
import { LockMyProducts } from "../MyProducts/LockMyProducts";
import { LockedFixedAsset } from "../FixedAsset/LockedFixedAsset";
import { PermissionBasedView } from "components/PermissionBasedView/PermissionBasedView";
import Payroll from "../Payroll/Payroll";
import { RevenueScheduleHome } from "../RevenueSchedule/RevenueSchedule";
import { ExpenseScheduleHome } from "../ExpenseSchedule/ExpenseSchedule";
import { RevenueMetricsLocked } from "../RevenueMetrics/RevenueMetricsLocked";
import { AllReports } from "../AllReports/AllReports";
import { BusinessReviewPage } from "../Reports/BusinessReview";
import ConditionalToolTip from "components/design/conditionalToolTip";

const PERMISSION_ROUTES = [
  { key: "HOME", path: "/books" },
  { key: "DATA_SOURCES", path: "/books/data-sources" },
  { key: "TRANSACTIONS", path: "/books/transactions" },
  { key: "FINANCIALS", path: "/books/live-financials" },
  { key: "REVENUE", path: "/books/invoices-and-customers" },
  { key: "EXPENSE", path: "/books/expenses/vendors" },
  { key: "ACCOUNTING", path: "/books/categories" },
];

const OTHERS_REPORT_PATHS = [
  "/live-financials/others",
  "/live-financials/aging-summary",
  "/live-financials/ap-aging",
  "/live-financials/vendor-summary",
  "/live-financials/business-review",
  "/live-financials/custom-report",
  "/books/custom-reports",
  "/books/categories",
  "/books/trial-balance",
];

const PermissionRouteGuard = ({
  permissionKey,
  children,
}: {
  permissionKey: string;
  children: React.ReactNode;
}) => {
  const { isCustomer } = useRoleBasedView();
  const { isSuperAdmin, isLoading, permissions } = useMyPermissions();
  const { search } = useLocation();

  if (!isCustomer || isSuperAdmin || isLoading) return <>{children}</>;

  const perm = permissions.find((p) => p.permission_key === permissionKey);
  const hasAccess = Boolean(perm) && perm!.access_level !== "NO_ACCESS";

  if (!hasAccess) {
    const firstAccessible = PERMISSION_ROUTES.find(({ key }) => {
      const p = permissions.find((perm) => perm.permission_key === key);
      return Boolean(p) && p!.access_level !== "NO_ACCESS";
    });

    const redirectPath = firstAccessible
      ? firstAccessible.path
      : "/books/intragroup/transfers";

    return <Redirect to={`${redirectPath}${search}`} />;
  }

  return <>{children}</>;
};

const ONBOARDING_TOOLTIP =
  "Complete the setup to unlock all navigation and start using Inkle Books";

const OnboardingLock = ({
  children,
  isLocked,
}: {
  children: React.ReactNode;
  isLocked: boolean;
}) => {
  if (!isLocked) return <>{children}</>;

  return (
    <ConditionalToolTip
      condition={ONBOARDING_TOOLTIP}
      side="right"
      propClass="t-max-w-48"
    >
      <div className="t-opacity-50 t-select-none t-cursor-not-allowed">
        <div className="t-pointer-events-none">{children}</div>
      </div>
    </ConditionalToolTip>
  );
};

export const BooksNav = () => {
  const location = useLocation();
  const parsedSearch = parse(location.search, { ignoreQueryPrefix: true });
  const { isCustomer, isCpa } = useRoleBasedView();
  const { uuid: groupId, entities } = useCurrentGroupContext();
  const {
    isSuperAdmin,
    isLoading: permissionsLoading,
    permissions,
  } = useMyPermissions();

  const canSeeNav = (key: string) => {
    if (!isCustomer || isSuperAdmin) return true;
    if (permissionsLoading) return true;
    const perm = permissions.find((p) => p.permission_key === key);
    return Boolean(perm) && perm!.access_level !== "NO_ACCESS";
  };

  const search = stringify(
    {
      entity: parsedSearch.entity,
      company: parsedSearch.company,
    },
    { skipNulls: true, addQueryPrefix: true }
  );

  const generalLedgerLink = `/categories${
    search ? `${search}&` : "?"
  }hideZero=true`;

  const isValidIntragroup = entities?.some(
    (entity) => entity.country_code === INDIA_COUNTRY_CODE
  );

  const shouldShowTaxLink = isCpa && Boolean(groupId);

  return (
    <div className="t-h-full t-flex t-flex-col t-justify-between t-overflow-hidden">
      <div className="t-overflow-y-auto">
        {canSeeNav("HOME") && (
          <LeftBarItem exact to={search} icon={HomeIcon}>
            Home
          </LeftBarItem>
        )}
        {canSeeNav("DATA_SOURCES") && (
          <OnboardingLock isLocked={false}>
            <LeftBarItem to={`/data-sources${search}`} icon={DataSourcesIcon}>
              Data Sources
            </LeftBarItem>
          </OnboardingLock>
        )}
        <OnboardingLock isLocked={false}>
          {canSeeNav("TRANSACTIONS") && (
            <LeftBarItem to={`/transactions${search}`} icon={TransactionsIcon}>
              Transactions
            </LeftBarItem>
          )}
          {canSeeNav("FINANCIALS") && (
            <SubMenu.Root
              title="Reports"
              defaultRoute={`/live-financials${search}`}
              childPaths={[
                "/live-financials",
                "/financial-closing",
                "/all-reports",
                "/custom-reports",
              ]}
            >
              <SubMenu.Trigger icon={FinancialsIcon}>Reports</SubMenu.Trigger>
              <SubMenu.Container hoverTitle="Reports">
                <SubMenu.Item
                  to={`/live-financials${search}`}
                  useParentPath
                  exact
                >
                  Summary
                </SubMenu.Item>
                <SubMenu.Item
                  to={`/live-financials/balance-sheet${search}`}
                  useParentPath
                >
                  Balance sheet
                </SubMenu.Item>
                <SubMenu.Item
                  to={`/live-financials/income-statement${search}`}
                  useParentPath
                >
                  Income statement
                </SubMenu.Item>
                <SubMenu.Item
                  to={`/live-financials/cash-flow-statement${search}`}
                  useParentPath
                >
                  Cash Flow Statement
                </SubMenu.Item>
                <SubMenu.Item to={`/financial-closing${search}`} useParentPath>
                  Financial closing
                </SubMenu.Item>
                <SubMenu.Item to={`/all-reports${search}`} useParentPath>
                  Exports
                </SubMenu.Item>
                <SubMenu.Item
                  to={`/live-financials/others${search}`}
                  useParentPath
                  activePaths={OTHERS_REPORT_PATHS}
                >
                  Others
                </SubMenu.Item>
              </SubMenu.Container>
            </SubMenu.Root>
          )}

          {canSeeNav("REVENUE") && (
            <SubMenu.Root
              title="Revenue"
              childPaths={[
                "/revenue-metrics",
                "/invoices-and-customers",
                "/my-products",
                "/revenue-schedule",
                "revenue/customers",
              ]}
              defaultRoute={`/invoices-and-customers${search}`}
            >
              <SubMenu.Trigger icon={RevenueIcon}>Revenue</SubMenu.Trigger>
              <SubMenu.Container hoverTitle="Revenue">
                <SubMenu.Item
                  to={`/invoices-and-customers${search}`}
                  useParentPath
                >
                  Invoices
                </SubMenu.Item>
                <SubMenu.Item to={`/revenue-metrics${search}`} useParentPath>
                  Metrics
                </SubMenu.Item>
                <SubMenu.Item to={`/revenue/customers${search}`} useParentPath>
                  Customers
                </SubMenu.Item>
                <SubMenu.Item to={`/my-products${search}`} useParentPath>
                  My Products
                </SubMenu.Item>
                <SubMenu.Item to={`/revenue-schedule${search}`} useParentPath>
                  Revenue Recognition
                </SubMenu.Item>
              </SubMenu.Container>
            </SubMenu.Root>
          )}

          {canSeeNav("EXPENSE") && (
            <SubMenu.Root
              title="Expenses"
              childPaths={[
                "/expenses/vendors",
                "/bills-and-vendors",
                "/payroll",
                "/expense-schedule",
              ]}
              defaultRoute={`/expenses/vendors${search}`}
            >
              <SubMenu.Trigger icon={ExpensesIcon}>Expenses</SubMenu.Trigger>
              <SubMenu.Container hoverTitle="Expenses">
                <SubMenu.Item to={`/expenses/vendors${search}`} useParentPath>
                  Vendors
                </SubMenu.Item>
                <SubMenu.Item to={`/bills-and-vendors${search}`} useParentPath>
                  Bills
                </SubMenu.Item>
                <SubMenu.Item to={`/payroll${search}`} useParentPath>
                  Payroll
                </SubMenu.Item>
                <SubMenu.Item to={`/expense-schedule${search}`} useParentPath>
                  Expense Recognition
                </SubMenu.Item>
              </SubMenu.Container>
            </SubMenu.Root>
          )}

          {canSeeNav("ACCOUNTING") && (
            <SubMenu.Root
              title="Accounting"
              childPaths={[
                "/categories",
                "/trial-balance",
                "/chart-of-accounts",
                "/reconciliation",
                "/journal-entries",
                "/fixed-assets",
              ]}
              defaultRoute={generalLedgerLink}
            >
              <SubMenu.Trigger icon={Accounting}>Accounting</SubMenu.Trigger>
              <SubMenu.Container hoverTitle="Accounting">
                <SubMenu.Item to={generalLedgerLink} useParentPath>
                  General Ledger
                </SubMenu.Item>
                <SubMenu.Item to={`/trial-balance${search}`} useParentPath>
                  Trial Balance
                </SubMenu.Item>
                <SubMenu.Item to={`/chart-of-accounts${search}`} useParentPath>
                  Chart of Accounts
                </SubMenu.Item>
                <SubMenu.Item to={`/reconciliation${search}`} useParentPath>
                  Reconciliations
                </SubMenu.Item>
                <SubMenu.Item to={`/journal-entries${search}`} useParentPath>
                  Journal Entries
                </SubMenu.Item>
                <SubMenu.Item to={`/fixed-assets${search}`} useParentPath>
                  Fixed Assets
                </SubMenu.Item>
              </SubMenu.Container>
            </SubMenu.Root>
          )}
          {isValidIntragroup && (
            <SubMenu.Root
              title="Intragroup"
              defaultRoute={`/intragroup/transfers${search}`}
              childPaths={["/intragroup/transfers", "/intragroup/services"]}
            >
              <SubMenu.Trigger icon={Transfers}>Intragroup</SubMenu.Trigger>
              <SubMenu.Container hoverTitle="Intragroup">
                <SubMenu.Item
                  to={`/intragroup/transfers${search}`}
                  useParentPath
                >
                  Transfers
                </SubMenu.Item>
                <SubMenu.Item
                  to={`/intragroup/services${search}`}
                  useParentPath
                >
                  Services
                </SubMenu.Item>
              </SubMenu.Container>
            </SubMenu.Root>
          )}

          <LeftBarItem to={`/services${search}`} icon={BookkeepingServices}>
            Bookkeeping Services
          </LeftBarItem>

          <SubMenu.Root
            title="Books Settings"
            defaultRoute={`/books-settings${
              search ? `${search}&tab=accounting` : "?tab=accounting"
            }`}
            childPaths={["/books-settings"]}
          >
            <SubMenu.Trigger icon={BooksSettingsIcon}>
              Books Settings
            </SubMenu.Trigger>
            <SubMenu.Container hoverTitle="Books Settings">
              <SubMenu.Item
                to={`/books-settings${
                  search ? `${search}&tab=accounting` : "?tab=accounting"
                }`}
                useParentPath
              >
                Accounting
              </SubMenu.Item>
              <SubMenu.Item
                to={`/books-settings${
                  search ? `${search}&tab=rules` : "?tab=rules"
                }`}
                useParentPath
              >
                Rules
              </SubMenu.Item>
              <SubMenu.Item
                to={`/books-settings${
                  search
                    ? `${search}&tab=default-categories`
                    : "?tab=default-categories"
                }`}
                useParentPath
              >
                Default Categories
              </SubMenu.Item>
              <SubMenu.Item
                to={`/books-settings${
                  search
                    ? `${search}&tab=chart-of-accounts`
                    : "?tab=chart-of-accounts"
                }`}
                useParentPath
              >
                Chart of Accounts
              </SubMenu.Item>
              <SubMenu.Item
                to={`/books-settings${
                  search
                    ? `${search}&tab=classifications`
                    : "?tab=classifications"
                }`}
                useParentPath
              >
                Classifications
              </SubMenu.Item>
              <SubMenu.Item
                to={`/books-settings${
                  search ? `${search}&tab=invoices` : "?tab=invoices"
                }`}
                useParentPath
              >
                Invoices
              </SubMenu.Item>
              <SubMenu.Item
                to={`/books-settings${
                  search ? `${search}&tab=bills` : "?tab=bills"
                }`}
                useParentPath
              >
                Bills
              </SubMenu.Item>
            </SubMenu.Container>
          </SubMenu.Root>
        </OnboardingLock>

        {shouldShowTaxLink && (
          <LeftBarItem
            useParentPath={false}
            to={`/admin/crm/${groupId}`}
            icon={FilingsIcon}
          >
            Tax
          </LeftBarItem>
        )}
      </div>
    </div>
  );
};

const BooksEntitySwitcher = ({
  isCustomer,
  isCpa,
  isForeignCA,
  onGroupLoading,
}: {
  isCustomer?: boolean;
  isCpa?: boolean;
  isForeignCA?: boolean;
  onGroupLoading: (loading: boolean) => void;
}) => {
  const handleGroupChange = ({ isLoading }: { isLoading: boolean }) =>
    onGroupLoading(isLoading);

  const { open: isLeftBarOpen, setOpen: setLeftBarOpen } = useLeftBar();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();

  const wasAutoExpandedRef = useRef(false);

  const handleEntitySwitcherFocus = () => {
    if (!isLeftBarOpen) {
      setLeftBarOpen?.(true);
      wasAutoExpandedRef.current = true;
    }
  };

  useEffect(() => {
    if (wasAutoExpandedRef.current) {
      setLeftBarOpen?.(false);
      wasAutoExpandedRef.current = false;
    }
  }, [groupId, entityId]);

  return (
    <div
      className="t-flex t-flex-col t-gap-2"
      onFocusCapture={handleEntitySwitcherFocus}
    >
      {(isCpa || isForeignCA) && (
        <GroupSelectorWithDefaultGroup
          size="small"
          onGroupChange={handleGroupChange}
        />
      )}
      <EntitySelector
        left={0}
        size="small"
        showOnlyUSEntities
        showGoToEntity={isCustomer}
      />
    </div>
  );
};

export const BooksDashboard = () => {
  const { path, url } = useRouteMatch();
  const location = useLocation();
  const parsedSearch = parse(location.search, { ignoreQueryPrefix: true });
  const { isCpa, isCustomer, isForeignCA } = useRoleBasedView();
  const { uuid: groupId, entities } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const history = useHistory();
  const { pathname } = useLocation();
  const [isLoading, setIsLoading] = React.useState(false);
  const { dispatch: currentAppDispatch } = useCurrentAppContext();

  const allFilters = useTransactionsFilter({
    searchTerm: null,
    pageNum: 1,
  });

  const prefetchTransactions = useTransactionApisPrefetch(
    "getAllTransactionsData"
  );
  const prefetchLedgerBanks = useBankConnectionsPrefetch("getEntityBanks");
  const prefetchLiveReports = useReportsPrefetch("newInteractiveReport");
  const prefetchConnections = useBooksConnectionsPrefetch("getAllConnections");

  const selectedEntity = useMemo(
    () => entities.find((entity) => entity.uuid === entityId),
    [entities, entityId]
  );

  useEffect(() => {
    if (entityId && groupId) {
      prefetchTransactions(allFilters);
      prefetchLedgerBanks({
        groupId,
        entityId,
      });
      prefetchConnections({
        groupId,
        entityId,
      });
    }
  }, [allFilters, entityId]);

  useEffect(() => {
    if (selectedEntity?.country_id === process.env.PUBLIC_US_COUNTRY_ID) {
      prefetchLiveReports({
        groupId,
        entityId,
        reportType: "BALANCE_SHEET",
        accountingMethod: "CASH",
        startDate: dayjs().startOf("year").format(YYYY_MM_DD),
        endDate: dayjs().format(YYYY_MM_DD),
        // @ts-ignore
        showNonZeroAccounts: true,
        comparison_report_type: "TOTALS",
      });
    }
  }, [selectedEntity]);

  const search = stringify(
    {
      entity: parsedSearch.entity,
      company: parsedSearch.company,
    },
    { skipNulls: true, addQueryPrefix: true }
  );

  useEffect(() => {
    currentAppDispatch({
      type: "SET_CURRENT_APP",
      payload: { currentApp: isCustomer ? "Books" : "Books Admin" },
    });
  }, [isCustomer]);

  const { trackEvent } = useAnalytics();

  const transactionsPath = `${path}/transactions`;

  const isTransactions = matchPath(pathname, {
    path: transactionsPath,
    exact: false,
  });

  const stickyNavGroup = (
    <SidebarStickyGroup
      tickets={
        (isCpa || isForeignCA) && (
          <button
            className="all:unset t-w-full"
            onClick={() => trackEvent(ACTION_ITEMS_CLICKED_FROM_SIDEBAR)}
          >
            <LeftBarItem to={`/tickets${search}`} icon={Ticket}>
              Tickets
            </LeftBarItem>
          </button>
        )
      }
      chat={
        <button
          className="all:unset t-w-full"
          onClick={() => trackEvent(CHAT_CLICKED_FROM_SIDEBAR)}
        >
          <ChatLeftNav
            to={
              isCpa || isForeignCA
                ? `/chat/unreplied${search}`
                : `/chat${search}`
            }
          />
        </button>
      }
      documents={
        <button
          className="all:unset t-w-full"
          onClick={() => trackEvent(DOCUMENTS_CLICKED_FROM_SIDEBAR)}
        >
          <LeftBarItem to={`/documents${search}`} icon={DocumentIcon}>
            Documents
          </LeftBarItem>
        </button>
      }
    />
  );

  return (
    <AppLayout
      noVerticalGap={Boolean(isTransactions)}
      sidebar={
        <LeftBar
          useInternalLink
          showCollapse
          logo={<LeftBarLogo logo={InkleBooksLogo} symbol={InkleBooksSymbol} />}
          subHeader={
            <BooksEntitySwitcher
              isCustomer={isCustomer}
              isCpa={isCpa}
              isForeignCA={isForeignCA}
              onGroupLoading={setIsLoading}
            />
          }
          stickyNav={stickyNavGroup}
        >
          <BooksNav />
        </LeftBar>
      }
      topbar={
        <TopBar
          border
          logo={<LeftBarLogo logo={InkleBooksLogo} symbol={InkleBooksSymbol} />}
          subHeader={
            <>
              {(isForeignCA || isCpa) && (
                <GroupSelectorWithDefaultGroup
                  size="small"
                  onGroupChange={({ isLoading }) => setIsLoading(isLoading)}
                />
              )}
              <EntitySelector right={0} size="small" showOnlyUSEntities />
            </>
          }
          nav={<BooksNav />}
          stickyNav={stickyNavGroup}
          useInternalLink
        />
      }
      banner={
        <Switch>
          <RestrictedRoute path={transactionsPath}>
            <></>
          </RestrictedRoute>
          <RestrictedRoute path={path}>
            <DashboardBanner />
          </RestrictedRoute>
        </Switch>
      }
    >
      <QuickNavModal pages={BOOKS_QUICK_NAV_PAGES} />
      {isLoading ? (
        <Loader />
      ) : (
        <Switch>
          <Route
            path={`${path}/transactions/reconciliation/start/:bankAccountId`}
          >
            {(props) => (
              <Redirect
                to={`${path}/reconciliation/start/${props.match?.params.bankAccountId}${search}`}
              />
            )}
          </Route>
          <Route
            path={`${path}/data-sources/reconciliation/start/:bankAccountId`}
          >
            {(props) => (
              <Redirect
                to={`${path}/reconciliation/start/${props.match?.params.bankAccountId}${search}`}
              />
            )}
          </Route>
          <Redirect
            from={`${path}/transactions/reconciliation`}
            to={`${path}/reconciliation${search}`}
          />
          <Redirect
            from={`${path}/connections`}
            to={`${path}/data-sources${search}`}
          />
          <RestrictedRoute path={transactionsPath}>
            <PermissionRouteGuard permissionKey="TRANSACTIONS">
              <TransactionsPage />
            </PermissionRouteGuard>
          </RestrictedRoute>
          <RestrictedRoute
            exact
            path={`${path}/services/:taskId/:financialClosingId`}
          >
            {({ match }) => {
              return (
                <FinancialClosingDetails
                  breadcrumbs={[
                    {
                      name: "Service",
                      link: `/books/services/${match?.params.taskId}${search}`,
                    },
                  ]}
                  close={() =>
                    history.push(
                      `/books/services/${match?.params.taskId}${search}`
                    )
                  }
                />
              );
            }}
          </RestrictedRoute>
          <RestrictedRoute path={`${path}/services`}>
            <Services />
          </RestrictedRoute>
          <RestrictedRoute
            exact
            path={`${path}/reconciliation/:reconcilationId`}
          >
            <ReconciliationTxn />
          </RestrictedRoute>
          <RestrictedRoute
            exact
            path={`${path}/ai-reconciliation/:reconcilationId`}
          >
            <AIReconciliation />
          </RestrictedRoute>
          <RestrictedRoute path={`${path}/financial-closing`}>
            <PermissionRouteGuard permissionKey="FINANCIALS">
              <FinancialClosing />
            </PermissionRouteGuard>
          </RestrictedRoute>
          <RestrictedRoute exact path={`${path}/merchants`}>
            <Redirect to={`${url}/bills-and-vendors${search}`} />
          </RestrictedRoute>
          <Route
            path={`${path}/invoices-and-customers/customers/:customerId`}
            render={({ match }) => (
              <Redirect
                to={`${path}/revenue/customers/${match.params.customerId}${search}`}
              />
            )}
          />
          <RestrictedRoute
            exact
            path={`${path}/invoices-and-customers/:invoiceId/send`}
          >
            <PermissionRouteGuard permissionKey="REVENUE">
              <InvoiceSendPage />
            </PermissionRouteGuard>
          </RestrictedRoute>
          <RestrictedRoute path={`${path}/invoices-and-customers`}>
            <PermissionRouteGuard permissionKey="REVENUE">
              <InvoicesHome />
            </PermissionRouteGuard>
          </RestrictedRoute>
          <RestrictedRoute path={`${path}/revenue/customers`}>
            <PermissionRouteGuard permissionKey="REVENUE">
              <Customers />
            </PermissionRouteGuard>
          </RestrictedRoute>
          <RestrictedRoute path={`${path}/invoicing`}>
            <Redirect to={`${url}/invoices-and-customers${search}`} />
          </RestrictedRoute>
          <Redirect
            from={`${path}/categories/journalentry`}
            to={`${path}/journal-entries${search}`}
          />
          <Redirect
            from={`${path}/data-sources/reconciliation`}
            to={`${path}/reconciliation${search}`}
          />
          <RestrictedRoute path={`${path}/journal-entries`}>
            <PermissionRouteGuard permissionKey="ACCOUNTING">
              <JournalEntries />
            </PermissionRouteGuard>
          </RestrictedRoute>
          <RestrictedRoute path={`${path}/reconciliation`}>
            <PermissionRouteGuard permissionKey="ACCOUNTING">
              <Reconciliation />
            </PermissionRouteGuard>
          </RestrictedRoute>
          <RestrictedRoute path={`${path}/trial-balance`}>
            <PermissionRouteGuard permissionKey="ACCOUNTING">
              <TrialBalancePage />
            </PermissionRouteGuard>
          </RestrictedRoute>
          <RestrictedRoute path={`${path}/chart-of-accounts`}>
            <PermissionRouteGuard permissionKey="ACCOUNTING">
              <ChartOfAccounts />
            </PermissionRouteGuard>
          </RestrictedRoute>
          <RestrictedRoute path={`${path}/categories`}>
            <PermissionRouteGuard permissionKey="ACCOUNTING">
              <GeneralLedgerApp />
            </PermissionRouteGuard>
          </RestrictedRoute>
          <RestrictedRoute path={`${path}/data-sources`}>
            <PermissionRouteGuard permissionKey="DATA_SOURCES">
              <DataSources />
            </PermissionRouteGuard>
          </RestrictedRoute>
          <RestrictedRoute
            path={`${path}/customers/:customerId`}
            render={({ match }) => {
              return (
                <Redirect
                  to={`/books/invoices-and-customers/customers/${match.params.customerId}${search}`}
                />
              );
            }}
          />
          <RestrictedRoute path={`${path}/customers`}>
            <Redirect to={`${url}/invoices-and-customers/customers${search}`} />
          </RestrictedRoute>
          <Redirect
            from={`${path}/bills-and-vendors/vendors`}
            to={`${path}/expenses/vendors${search}`}
          />
          <Redirect
            from={`${path}/bills-and-vendors/vendors/:vendorId`}
            to={`${path}/expenses/vendors/:vendorId${search}`}
          />
          <Route path={`${path}/expenses/vendors/:vendorId`}>
            <PermissionRouteGuard permissionKey="EXPENSE">
              <VendorDetailPage
                breadcrumb={[
                  {
                    link: `${url}/expenses/vendors${search}`,
                    name: "Vendor",
                  },
                ]}
              />
            </PermissionRouteGuard>
          </Route>
          <RestrictedRoute path={`${path}/bills-and-vendors`}>
            <PermissionRouteGuard permissionKey="EXPENSE">
              <BillsAndVendors />
            </PermissionRouteGuard>
          </RestrictedRoute>
          <RestrictedRoute path={`${path}/expenses/vendors`}>
            <PermissionRouteGuard permissionKey="EXPENSE">
              <Vendors />
            </PermissionRouteGuard>
          </RestrictedRoute>
          <RestrictedRoute path={`${path}/vendors`}>
            <Redirect to={`${url}/bills-and-vendors/vendors${search}`} />
          </RestrictedRoute>
          <RestrictedRoute path={`${path}/customers-and-vendors`}>
            <Redirect to={`${url}/bills-and-vendors/vendors${search}`} />
          </RestrictedRoute>
          <RestrictedRoute path={`${path}/payroll`}>
            <PermissionRouteGuard permissionKey="EXPENSE">
              <Payroll />
            </PermissionRouteGuard>
          </RestrictedRoute>
          <RestrictedRoute path={`${path}/my-products`}>
            <PermissionRouteGuard permissionKey="REVENUE">
              <PermissionBasedView
                route="/books/my-products"
                blockedUI={<LockMyProducts />}
              >
                <MyProducts />
              </PermissionBasedView>
            </PermissionRouteGuard>
          </RestrictedRoute>
          <RestrictedRoute path={`${path}/products-and-services`}>
            <Redirect to={`${url}/my-products${search}`} />
          </RestrictedRoute>
          <RestrictedRoute path={`${path}/revenue-metrics`}>
            <PermissionRouteGuard permissionKey="REVENUE">
              <PermissionBasedView
                route="/books/revenue-metrics"
                blockedUI={<RevenueMetricsLocked />}
              >
                <RevenueMetrics />
              </PermissionBasedView>
            </PermissionRouteGuard>
          </RestrictedRoute>
          <Redirect
            from={`${path}/live-financials/saved-reports`}
            to={`${path}/saved-reports${search}`}
          />
          <Redirect
            from={`${path}/live-financials/custom-reports/:reportId`}
            to={`${path}/live-financials/custom-report/:reportId`}
          />
          <RestrictedRoute path={`${path}/live-financials`}>
            <PermissionRouteGuard permissionKey="FINANCIALS">
              <Reports />
            </PermissionRouteGuard>
          </RestrictedRoute>
          <RestrictedRoute exact path={`${path}/custom-reports/new`}>
            <PermissionRouteGuard permissionKey="FINANCIALS">
              <NewCustomReportTemplate />
            </PermissionRouteGuard>
          </RestrictedRoute>
          <Route exact path={`${path}/custom-reports/:reportId`}>
            <PermissionRouteGuard permissionKey="FINANCIALS">
              <CustomReportViewOnly />
            </PermissionRouteGuard>
          </Route>
          <RestrictedRoute path={`${path}/custom-reports`}>
            <PermissionRouteGuard permissionKey="FINANCIALS">
              <CustomReports />
            </PermissionRouteGuard>
          </RestrictedRoute>
          <Redirect
            from={`${path}/live-financial/:reportId`}
            to={`${path}/saved-reports/:reportId/${search}`}
          />
          <Route path={`${path}/saved-reports/:reportId`}>
            <PermissionRouteGuard permissionKey="FINANCIALS">
              <ReportViewWithId />
            </PermissionRouteGuard>
          </Route>
          <RestrictedRoute path={`${path}/saved-reports`}>
            <PermissionRouteGuard permissionKey="FINANCIALS">
              <SavedReport />
            </PermissionRouteGuard>
          </RestrictedRoute>
          <Route path={`${path}/fixed-assets`}>
            <PermissionBasedView
              route={`/books/fixed-assets`}
              blockedUI={<LockedFixedAsset />}
            >
              <FixedAsset />
            </PermissionBasedView>
          </Route>
          <Redirect
            from={`${path}/accounting`}
            to={`${path}/categories${search}`}
          />
          <Redirect
            from={`${path}/reports`}
            to={`${path}/live-financials${search}`}
          />{" "}
          <Redirect from={`${path}/metrics`} to={`${path}${search}`} />
          <Redirect
            from={`${path}/report`}
            to={`${path}/live-financial${search}`}
          />
          <Redirect
            from={`${path}/report/:reportId`}
            to={`${path}/live-financial/:reportId${search}`}
          />
          {(isCpa || isForeignCA) && (
            <RestrictedRoute path={`${path}/transfers/transfer/`}>
              <InitiateTransferModal to={`${path}/transfers`} />
            </RestrictedRoute>
          )}
          <RestrictedRoute path={`${path}/intragroup/transfers/transfer/`}>
            <InitiateTransferModal to={`${path}/intragroup/transfers`} />
          </RestrictedRoute>
          {(isCpa || isForeignCA) && (
            <RestrictedRoute path={`${path}/transfers/`}>
              <Invoices />
            </RestrictedRoute>
          )}
          <RestrictedRoute path={`${path}/intragroup/transfers`}>
            <Invoices />
          </RestrictedRoute>
          <RestrictedRoute path={`${path}/intragroup/services`}>
            <IntragroupServices />
          </RestrictedRoute>
          <RestrictedRoute exact path={`${path}/dashboard`}>
            <Redirect to={path} />
          </RestrictedRoute>
          {isCustomer && (
            <RestrictedRoute exact path={`${path}/action-items`}>
              <Redirect to={`${path}/tickets`} />
            </RestrictedRoute>
          )}
          <RestrictedRoute
            exact
            path={`${path}/books-settings/custom-reports/new`}
          >
            <NewCustomReportTemplate />
          </RestrictedRoute>
          <RestrictedRoute path={`${path}/books-settings`}>
            <BooksSettings showEntitySelector={false} />
          </RestrictedRoute>
          <RestrictedRoute path={`${path}/all-reports/financial-review`}>
            <PermissionRouteGuard permissionKey="FINANCIALS">
              <BusinessReviewPage />
            </PermissionRouteGuard>
          </RestrictedRoute>
          <RestrictedRoute exact path={`${path}/all-reports`}>
            <PermissionRouteGuard permissionKey="FINANCIALS">
              <AllReports />
            </PermissionRouteGuard>
          </RestrictedRoute>
          <RestrictedRoute exact path={`${path}/monthly-closing`}>
            <Redirect to={`${path}/financial-closing${search}`} />
          </RestrictedRoute>
          <RestrictedRoute path={`${path}/revenue-schedule`}>
            <PermissionRouteGuard permissionKey="REVENUE">
              <RevenueScheduleHome />
            </PermissionRouteGuard>
          </RestrictedRoute>
          <RestrictedRoute path={`${path}/expense-schedule`}>
            <PermissionRouteGuard permissionKey="EXPENSE">
              <ExpenseScheduleHome />
            </PermissionRouteGuard>
          </RestrictedRoute>
          <RestrictedRoute exact path={path}>
            <PermissionRouteGuard permissionKey="HOME">
              <BooksHome />
            </PermissionRouteGuard>
          </RestrictedRoute>
          <RoutesAccrossApps />
        </Switch>
      )}
    </AppLayout>
  );
};
